/**
 * Shared plan price line for every surface that states a plan's price: the
 * Super Admin plan card, the public /pricing cards and the signup plan
 * picker. A plan can carry BOTH a flat monthly fee and a per-active-store
 * price; this is the one place that decides how they read, so the surfaces
 * cannot drift.
 *
 * Inputs are integer cents. Only nonzero parts are shown, so a plan with a
 * flat fee and no per-store price never prints "$0.00/active store". When
 * nothing is nonzero, a real $0 (not null) reads "$0.00/mo"; no data at all
 * yields no parts and an empty line.
 */

const MONTHLY_UNIT = "/mo";
const ACTIVE_STORE_UNIT = "/active store";

export function formatCents(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(cents / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

const part = (cents, unit) => ({ cents, unit, text: `${formatCents(cents)}${unit}` });

export function planPriceParts({ monthlyCents = null, perActiveCents = null } = {}) {
    const parts = [];
    if (monthlyCents != null && monthlyCents !== 0) parts.push(part(monthlyCents, MONTHLY_UNIT));
    if (perActiveCents != null && perActiveCents !== 0) {
        parts.push(part(perActiveCents, ACTIVE_STORE_UNIT));
    }
    if (parts.length) return parts;
    // Nothing nonzero: a genuine $0 still states itself.
    if (monthlyCents === 0) return [part(0, MONTHLY_UNIT)];
    if (perActiveCents === 0) return [part(0, ACTIVE_STORE_UNIT)];
    return [];
}

export function planPriceLine(args) {
    return planPriceParts(args)
        .map((p) => p.text)
        .join(" + ");
}
